import { consentRevision } from './content';

export const API_CONTRACT_VERSION = 'sparksoft-public-v1';
export const API_CONTRACT_SHA256 = 'ee42d4532a389815f443cf2b76f6ce7c92894f180ded090e0a316a006ccfeba9';

export type ContactKind = 'email' | 'phone';
export type ErrorCode = 'MALFORMED_REQUEST' | 'CONSENT_REVISION_STALE' | 'IDEMPOTENCY_CONFLICT' | 'VALIDATION_ERROR' | 'SUBMISSION_REJECTED' | 'RATE_LIMITED' | 'TEMPORARY_UNAVAILABLE';

export interface LeadDraft {
  name: string;
  contact: string;
  message: string;
}

export interface ApiErrorPayload {
  error: {
    code: ErrorCode;
    message: string;
    fields?: Record<string, string>;
    requestId: string;
  };
}

export interface ContactChallenge {
  token: string;
  expiresAt: string;
}

export class ContactApiError extends Error {
  code?: ErrorCode;
  fields?: Record<string, string>;
  retryAfter?: string;

  constructor(message: string, init: Partial<ContactApiError> = {}) {
    super(message);
    Object.assign(this, init);
  }
}

export const deriveContactKind = (value: string): ContactKind => (value.includes('@') ? 'email' : 'phone');

async function readJson(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

async function apiError(response: Response): Promise<ContactApiError> {
  const payload = (await readJson(response)) as ApiErrorPayload | null;
  const safeMessage = payload?.error?.message || 'Не удалось отправить обращение. Попробуйте еще раз.';
  return new ContactApiError(safeMessage, {
    code: payload?.error?.code,
    fields: payload?.error?.fields,
    retryAfter: response.headers.get('Retry-After') || undefined,
  });
}

export async function createContactChallenge(signal?: AbortSignal): Promise<ContactChallenge> {
  const challengeResponse = await fetch('/api/v1/contact/challenge', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: '{}',
    credentials: 'same-origin',
    signal,
  });
  if (!challengeResponse.ok) throw await apiError(challengeResponse);
  const challenge = (await readJson(challengeResponse)) as { token?: unknown; expiresAt?: unknown } | null;
  if (typeof challenge?.token !== 'string' || typeof challenge.expiresAt !== 'string') {
    throw new ContactApiError('Сервис формы временно недоступен. Попробуйте еще раз.');
  }
  return { token: challenge.token, expiresAt: challenge.expiresAt };
}

export async function submitContact(draft: LeadDraft, idempotencyKey: string, challengeToken: string, signal?: AbortSignal): Promise<void> {
  const leadResponse = await fetch('/api/v1/leads', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'Idempotency-Key': idempotencyKey,
    },
    body: JSON.stringify({
      name: draft.name.trim(),
      contact: { kind: deriveContactKind(draft.contact.trim()), value: draft.contact.trim() },
      message: draft.message.trim(),
      consent: { accepted: true, revision: consentRevision },
      antiSpam: { challengeToken, website: '' },
    }),
    credentials: 'same-origin',
    signal,
  });
  if (!leadResponse.ok) throw await apiError(leadResponse);
  const accepted = (await readJson(leadResponse)) as { status?: unknown; submissionId?: unknown } | null;
  if (accepted?.status !== 'accepted' || typeof accepted.submissionId !== 'string') {
    throw new ContactApiError('Ответ сервиса не удалось проверить. Напишите нам по email.');
  }
}
